import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";

import CartItem from "./CartItem";

const { Text } = Typography;

const PAGE_SIZE = 50;

const CartPanel = ({
  cart,
  totals,
  onIncrease,
  onDecrease,
  onQuantityChange,
  onCostChange,
  onRemove,
  onRemoveItems,
  onClear,
  isMobile,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("name");
  const [selectedIds, setSelectedIds] = useState([]);

  const filteredCart = useMemo(() => {
    const query = search.trim().toLowerCase();

    let result = cart.filter((item) => {
      if (!query) return true;

      return [
        item.partName,
        item.partNumber,
        item.importerId,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value)
            .toLowerCase()
            .includes(query)
        );
    });

    result = [...result].sort((a, b) => {
      if (sort === "name") {
        return String(a.partName).localeCompare(
          String(b.partName)
        );
      }

      if (sort === "quantity") {
        return b.quantity - a.quantity;
      }

      if (sort === "cost") {
        return b.unitCost - a.unitCost;
      }

      if (sort === "total") {
        return (
          b.quantity * b.unitCost -
          a.quantity * a.unitCost
        );
      }

      return 0;
    });

    return result;
  }, [cart, search, sort]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredCart.slice(
      start,
      start + PAGE_SIZE
    );
  }, [filteredCart, page]);

  const visibleIds = pageItems.map(
    (item) => item.sparepartId
  );

  const allSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) =>
      selectedIds.includes(id)
    );

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((current) =>
        current.filter(
          (id) => !visibleIds.includes(id)
        )
      );
    } else {
      setSelectedIds((current) => [
        ...new Set([
          ...current,
          ...visibleIds,
        ]),
      ]);
    }
  };

  const removeSelected = () => {
    if (!selectedIds.length) return;

    Modal.confirm({
      title: "Remove selected items?",
      content: `${selectedIds.length} item(s) will be removed from the purchase order.`,
      okText: "Remove",
      okButtonProps: {
        danger: true,
      },
      cancelText: "Cancel",
      onOk: () => {
        onRemoveItems(selectedIds);
        setSelectedIds([]);
      },
    });
  };

  const clearCart = () => {
    Modal.confirm({
      title: "Clear purchase cart?",
      content:
        "All items currently in this purchase order will be removed.",
      okText: "Clear Cart",
      okButtonProps: {
        danger: true,
      },
      cancelText: "Cancel",
      onOk: () => {
        onClear();
        setSelectedIds([]);
      },
    });
  };

  return (
    <Card
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Purchase Cart</span>

          <Tag color="blue">
            {totals.itemCount} items
          </Tag>
        </Space>
      }
      style={{
        borderRadius: 14,
      }}
    >
      {/* TOOLBAR */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile
            ? "column"
            : "row",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined />}
          placeholder="Search items in cart..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{
            flex: 1,
          }}
        />

        <Select
          value={sort}
          onChange={setSort}
          size="large"
          style={{
            width: isMobile
              ? "100%"
              : 180,
          }}
          options={[
            {
              value: "name",
              label: "Sort: Name",
            },
            {
              value: "quantity",
              label: "Sort: Quantity",
            },
            {
              value: "cost",
              label: "Sort: Unit Cost",
            },
            {
              value: "total",
              label: "Sort: Total",
            },
          ]}
        />
      </div>

      {/* BULK ACTIONS */}
      {cart.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 14,
            padding: "10px 12px",
            background: "#f8fafc",
            borderRadius: 10,
          }}
        >
          <Checkbox
            checked={allSelected}
            onChange={toggleAll}
          >
            Select visible
          </Checkbox>

          <Space wrap>
            {selectedIds.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={removeSelected}
              >
                Remove {selectedIds.length}
              </Button>
            )}

            <Button
              danger
              type="text"
              onClick={clearCart}
            >
              Clear Cart
            </Button>
          </Space>
        </div>
      )}

      {/* ITEMS */}
      {pageItems.length === 0 ? (
        <Empty
          description={
            cart.length === 0
              ? "Your purchase cart is empty"
              : "No matching cart items"
          }
          style={{
            padding: 50,
          }}
        />
      ) : (
        <div
          style={{
            maxHeight: isMobile
              ? "none"
              : 760,
            overflowY: isMobile
              ? "visible"
              : "auto",
            paddingRight: isMobile
              ? 0
              : 4,
          }}
        >
          {pageItems.map((item) => (
            <div
              key={item.sparepartId}
              style={{
                display: "flex",
                gap: 8,
              }}
            >
              <Checkbox
                checked={selectedIds.includes(
                  item.sparepartId
                )}
                onChange={() =>
                  setSelectedIds((current) =>
                    current.includes(
                      item.sparepartId
                    )
                      ? current.filter(
                          (id) =>
                            id !==
                            item.sparepartId
                        )
                      : [
                          ...current,
                          item.sparepartId,
                        ]
                  )
                }
                style={{
                  marginTop: 20,
                }}
              />

              <div style={{ flex: 1 }}>
                <CartItem
                  item={item}
                  onIncrease={onIncrease}
                  onDecrease={onDecrease}
                  onQuantityChange={
                    onQuantityChange
                  }
                  onCostChange={
                    onCostChange
                  }
                  onRemove={onRemove}
                  isMobile={isMobile}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {filteredCart.length > PAGE_SIZE && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 16,
          }}
        >
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={filteredCart.length}
            showSizeChanger={false}
            onChange={(nextPage) => {
              setPage(nextPage);
              setSelectedIds([]);
            }}
          />
        </div>
      )}

      <div
        style={{
          textAlign: "center",
          marginTop: 12,
        }}
      >
        <Text type="secondary">
          {filteredCart.length > 0
            ? `Showing ${Math.min(
                (page - 1) * PAGE_SIZE + 1,
                filteredCart.length
              )}–${Math.min(
                page * PAGE_SIZE,
                filteredCart.length
              )} of ${filteredCart.length}`
            : "No items"}
        </Text>
      </div>
    </Card>
  );
};

export default CartPanel;